import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Navigate, Link } from 'react-router-dom';
import DataTable from '../../components/DataTable';
import DetalleRespuestaModal from './DetalleRespuestaModal';
import useFetch from '../../hooks/useFetch';
import { downloadInformePdfWithErrorHandling, downloadInformeGrupoWithErrorHandling } from '../../utils/downloadInforme';
import styles from '../../styles/respuestas.module.css';
import encuestaStyles from '../../styles/encuestas.module.css';
import detalleStyles from '../../styles/detalleRespuestaModal.module.css';

const BASE = '/gestionEncuestas';
const PAGE_SIZE = 15;

const ESTADO_LABEL = {
  completado: 'Completado',
  en_progreso: 'En progreso',
  no_iniciado: 'No iniciado',
};

const ESTADO_ORDEN = { completado: 0, en_progreso: 1, no_iniciado: 2 };

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const truncarTexto = (valor, max) => {
  const txt = String(valor || '').trim();
  if (!txt) return '—';
  return txt.length > max ? `${txt.slice(0, max - 1)}…` : txt;
};

const formatFecha = (value) => {
  if (value == null || value === '') return '—';
  const str = typeof value === 'string' ? value : (value && value.toISOString?.().slice(0, 10));
  if (!str) return '—';
  const [y, m, d] = str.slice(0, 10).split('-').map(Number);
  const mes = MESES[m - 1] || '';
  return `${d} ${mes} ${y}`;
};

const Respuestas = () => {
  const [searchParams] = useSearchParams();
  const encuestaIdFromUrl = searchParams.get('encuestaId');
  const { execute } = useFetch();

  const [encuestaInfo, setEncuestaInfo] = useState(null);
  const [loadingEncuesta, setLoadingEncuesta] = useState(true);
  const [resultados, setResultados] = useState([]);
  const [modo, setModo] = useState('completo');
  const [resumenServidor, setResumenServidor] = useState(null);
  const [totalFilas, setTotalFilas] = useState(0);
  const [totalPaginasServidor, setTotalPaginasServidor] = useState(1);
  const [loadingResultados, setLoadingResultados] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [orden, setOrden] = useState('nombre_asc');
  const [pagina, setPagina] = useState(1);

  const [detalle, setDetalle] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [exportandoInforme, setExportandoInforme] = useState(false);
  const [tipoExportacionInforme, setTipoExportacionInforme] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [accionLoading, setAccionLoading] = useState(null);
  const [resetConfirmRow, setResetConfirmRow] = useState(null);

  const accionKey = (tipo, idUsuario) => `${tipo}:${idUsuario}`;
  const isAccionLoading = (tipo, idUsuario) => accionLoading === accionKey(tipo, idUsuario);

  const puedeReenviarFila = (row) => {
    if (!encuestaInfo || !row) return false;
    const estado = encuestaInfo.estado;
    if (estado !== 'activa' && estado !== 'programada') return false;
    if (estado === 'activa' && (row.porcentaje ?? 0) >= 100) return false;
    return true;
  };

  const puedeReiniciarFila = (row) => {
    if (!encuestaInfo || !row) return false;
    if (encuestaInfo.estado !== 'activa') return false;
    const idEquipo = row.id_equipo ?? encuestaInfo.id_equipo;
    if (!idEquipo) return false;
    return (row.num_respuestas ?? 0) > 0;
  };

  useEffect(() => {
    if (!encuestaIdFromUrl) return;
    let cancelled = false;
    (async () => {
      setLoadingEncuesta(true);
      try {
        const res = await execute(`${BASE}/encuestas`, 'GET');
        const list = Array.isArray(res?.data) ? res.data : [];
        const enc = list.find((e) => String(e.id_encuesta) === encuestaIdFromUrl) ?? null;
        if (!cancelled) setEncuestaInfo(enc);
      } catch (err) {
        if (!cancelled) {
          setEncuestaInfo(null);
          setFetchError(err?.message || 'No se pudo cargar la encuesta');
        }
      } finally {
        if (!cancelled) setLoadingEncuesta(false);
      }
    })();
    return () => { cancelled = true; };
  }, [encuestaIdFromUrl, execute]);

  const fetchResultados = useCallback(async (idEncuesta, opts = {}) => {
    if (!idEncuesta) {
      setResultados([]);
      setResumenServidor(null);
      return;
    }
    setLoadingResultados(true);
    setFetchError('');
    try {
      const res = await execute(`${BASE}/getResultadosEncuesta`, 'POST', {
        id_encuesta: Number(idEncuesta),
        pagina: opts.pagina ?? 1,
        tamano_pagina: PAGE_SIZE,
        busqueda: opts.busqueda ?? '',
        estado: opts.estado ?? 'todos',
        orden: opts.orden ?? 'nombre_asc',
      });
      const data = res?.data;
      if (Array.isArray(data)) {
        setModo('completo');
        setResultados(data);
        setResumenServidor(null);
        setTotalFilas(data.length);
        setTotalPaginasServidor(1);
        if (opts.pagina) setPagina(opts.pagina);
        return;
      }
      const filas = Array.isArray(data?.filas) ? data.filas : [];
      setModo(data?.modo === 'paginado' ? 'paginado' : 'completo');
      setResultados(filas);
      setResumenServidor(data?.resumen ?? null);
      setTotalFilas(data?.total_filas ?? filas.length);
      setTotalPaginasServidor(data?.total_paginas ?? 1);
      if (data?.pagina) setPagina(data.pagina);
    } catch (err) {
      setResultados([]);
      setResumenServidor(null);
      setFetchError(err?.message || 'No se pudieron cargar los resultados');
    } finally {
      setLoadingResultados(false);
    }
  }, [execute]);

  const fetchParams = useCallback(() => ({
    pagina,
    busqueda,
    estado: filtroEstado,
    orden,
  }), [pagina, busqueda, filtroEstado, orden]);

  useEffect(() => {
    if (!encuestaIdFromUrl) return;
    setPagina(1);
    setBusqueda('');
    setFiltroEstado('todos');
    setOrden('nombre_asc');
    fetchResultados(encuestaIdFromUrl, {
      pagina: 1,
      busqueda: '',
      estado: 'todos',
      orden: 'nombre_asc',
    });
  }, [encuestaIdFromUrl, fetchResultados]);

  useEffect(() => {
    if (!encuestaIdFromUrl || modo !== 'paginado') return undefined;
    const timer = window.setTimeout(() => {
      fetchResultados(encuestaIdFromUrl, fetchParams());
    }, busqueda ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [modo, encuestaIdFromUrl, pagina, busqueda, filtroEstado, orden, fetchResultados, fetchParams]);

  useEffect(() => {
    setPagina(1);
  }, [busqueda, filtroEstado, orden, encuestaIdFromUrl]);

  const resumen = useMemo(() => {
    if (resumenServidor) {
      return {
        total: resumenServidor.total ?? 0,
        completados: resumenServidor.completados ?? 0,
        enProgreso: resumenServidor.en_progreso ?? 0,
        noIniciados: resumenServidor.no_iniciados ?? 0,
      };
    }
    const total = resultados.length;
    const completados = resultados.filter((r) => r.estado === 'completado').length;
    const enProgreso = resultados.filter((r) => r.estado === 'en_progreso').length;
    const noIniciados = resultados.filter((r) => r.estado === 'no_iniciado').length;
    return { total, completados, enProgreso, noIniciados };
  }, [resumenServidor, resultados]);

  const filtradosOrdenados = useMemo(() => {
    if (modo === 'paginado') return resultados;
    let list = [...resultados];
    const q = busqueda.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        (r.nombre_completo || '').toLowerCase().includes(q)
      );
    }
    if (filtroEstado !== 'todos') {
      list = list.filter((r) => r.estado === filtroEstado);
    }
    list.sort((a, b) => {
      switch (orden) {
        case 'nombre_desc':
          return (b.nombre_completo || '').localeCompare(a.nombre_completo || '', 'es');
        case 'porcentaje_desc':
          return (b.porcentaje ?? 0) - (a.porcentaje ?? 0);
        case 'porcentaje_asc':
          return (a.porcentaje ?? 0) - (b.porcentaje ?? 0);
        case 'estado':
          return (ESTADO_ORDEN[a.estado] ?? 9) - (ESTADO_ORDEN[b.estado] ?? 9);
        case 'nombre_asc':
        default:
          return (a.nombre_completo || '').localeCompare(b.nombre_completo || '', 'es');
      }
    });
    return list;
  }, [modo, resultados, busqueda, filtroEstado, orden]);

  const totalPaginas = modo === 'paginado'
    ? totalPaginasServidor
    : Math.max(1, Math.ceil(filtradosOrdenados.length / PAGE_SIZE));
  const paginaActual = Math.min(pagina, totalPaginas);
  const filaPagina = useMemo(() => {
    if (modo === 'paginado') return filtradosOrdenados;
    const start = (paginaActual - 1) * PAGE_SIZE;
    return filtradosOrdenados.slice(start, start + PAGE_SIZE);
  }, [modo, filtradosOrdenados, paginaActual]);

  const closeDetalle = () => {
    setDetalle(null);
    setSelectedRow(null);
    setLoadingDetalle(false);
  };

  const openDetalle = async (row) => {
    setSelectedRow(row);
    setDetalle(null);
    setLoadingDetalle(true);
    try {
      const res = await execute(`${BASE}/getDetalleUsuarioEncuesta`, 'POST', {
        id_encuesta: Number(encuestaIdFromUrl),
        id_usuario: row.id_usuario,
      });
      if (res?.data) setDetalle(res.data);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const handleReenviarCorreo = async (row) => {
    if (!encuestaIdFromUrl || !row || accionLoading) return;
    setAccionLoading(accionKey('reenviar', row.id_usuario));
    setFetchError('');
    try {
      const res = await execute(`${BASE}/reenviarCorreoEncuesta`, 'POST', {
        id_encuesta: Number(encuestaIdFromUrl),
        id_usuario: row.id_usuario,
      });
      setSuccessMsg(res?.data?.message || `Correo reenviado a ${row.nombre_completo}`);
      window.setTimeout(() => setSuccessMsg(''), 4000);
      await fetchResultados(encuestaIdFromUrl, fetchParams());
    } catch (err) {
      setFetchError(err?.message || 'No se pudo reenviar el correo');
    } finally {
      setAccionLoading(null);
    }
  };

  const handleReiniciarEncuesta = async (row) => {
    if (!encuestaIdFromUrl || !row || accionLoading) return;
    const idEquipo = row.id_equipo ?? encuestaInfo?.id_equipo;
    if (!idEquipo) return;
    setAccionLoading(accionKey('reiniciar', row.id_usuario));
    setFetchError('');
    try {
      await execute(`${BASE}/reiniciarEncuestaUsuario`, 'POST', {
        id_encuesta: Number(encuestaIdFromUrl),
        id_usuario: row.id_usuario,
        id_equipo: idEquipo,
      });
      setSuccessMsg(`Respuestas de ${row.nombre_completo} reiniciadas correctamente.`);
      window.setTimeout(() => setSuccessMsg(''), 4000);
      setResetConfirmRow(null);
      if (selectedRow?.id_usuario === row.id_usuario) {
        setSelectedRow((prev) => (prev ? { ...prev, porcentaje: 0, num_respuestas: 0, estado: 'no_iniciado' } : null));
      }
      await fetchResultados(encuestaIdFromUrl, fetchParams());
      if (detalle && selectedRow?.id_usuario === row.id_usuario) {
        const res = await execute(`${BASE}/getDetalleUsuarioEncuesta`, 'POST', {
          id_encuesta: Number(encuestaIdFromUrl),
          id_usuario: row.id_usuario,
        });
        if (res?.data) setDetalle(res.data);
      }
    } catch (err) {
      setFetchError(err?.message || 'No se pudieron reiniciar las respuestas');
    } finally {
      setAccionLoading(null);
    }
  };

  const handleExportarInformeIndividual = async () => {
    if (!encuestaIdFromUrl || !detalle?.usuario?.id_usuario) return;

    setTipoExportacionInforme('individual');
    setExportandoInforme(true);
    setFetchError('');

    const result = await downloadInformePdfWithErrorHandling({
      id_encuesta: Number(encuestaIdFromUrl),
      id_usuario: detalle.usuario.id_usuario,
      nombreArchivoFallback: `Informe_4_Creatividades_${detalle.usuario.nombre_completo || 'participante'}.pdf`,
    });

    setExportandoInforme(false);
    setTipoExportacionInforme(null);

    if (!result.ok) {
      setFetchError(result.message);
    }
  };

  const handleExportarInformeGrupo = async () => {
    if (!encuestaIdFromUrl || encuestaInfo?.tipo !== 'equipo') return;

    setTipoExportacionInforme('grupal');
    setExportandoInforme(true);
    setFetchError('');

    const result = await downloadInformeGrupoWithErrorHandling({
      id_encuesta: Number(encuestaIdFromUrl),
      nombreArchivoFallback: `Informe Grupal 4 Creatividades_${encuestaInfo?.nombre_equipo || 'equipo'}.pptx`,
    });

    setExportandoInforme(false);
    setTipoExportacionInforme(null);

    if (!result.ok) {
      setFetchError(result.message);
    }
  };

  const puedeExportarInforme =
    encuestaInfo?.tipo === 'equipo' || Boolean(detalle?.usuario?.id_usuario);

  const columns = [
    {
      header: 'Usuario',
      accessor: 'nombre_completo',
      render: (row) => <span className={styles.usuarioCell}>{row.nombre_completo}</span>,
    },
    {
      header: 'Equipo',
      accessor: 'nombre_equipo',
      render: (row) => (
        <span className={styles.equipoCell} title={row.nombre_equipo || '—'}>
          {truncarTexto(row.nombre_equipo, 24)}
        </span>
      ),
    },
    {
      header: 'Respuestas',
      accessor: 'num_respuestas',
      render: (row) => (
        <span className={styles.pctText}>
          {row.num_respuestas ?? 0} / 40
        </span>
      ),
    },
    {
      header: '% completado',
      accessor: 'porcentaje',
      render: (row) => (
        <div className={styles.progressCell}>
          <div className={styles.progressBarBg}>
            <div
              className={styles.progressBarFill}
              style={{ width: `${Math.min(100, row.porcentaje ?? 0)}%` }}
            />
          </div>
          <span className={styles.pctText}>{row.porcentaje ?? 0}%</span>
        </div>
      ),
    },
    {
      header: 'Estado',
      accessor: 'estado',
      render: (row) => {
        const label = ESTADO_LABEL[row.estado] || row.estado;
        const cls =
          row.estado === 'completado'
            ? styles.badgeCompleted
            : row.estado === 'en_progreso'
              ? styles.badgeProgress
              : styles.badgeNotStarted;
        return <span className={`${styles.badge} ${cls}`}>{label}</span>;
      },
    },
    {
      header: 'Correos',
      accessor: 'conteo_correos',
      render: (row) => (
        <span
          className={
            (row.conteo_correos ?? 0) === 0 ? styles.conteoZero : styles.conteoNormal
          }
          style={{ display: 'block', textAlign: 'center' }}
        >
          {row.conteo_correos ?? 0}
        </span>
      ),
    },
    {
      header: '',
      accessor: 'acciones',
      render: (row) => (
        <div className={styles.rowActionsCell} onClick={(e) => e.stopPropagation()}>
          <div className={styles.rowActionIcons}>
            {puedeReenviarFila(row) && (
              <button
                type="button"
                className={styles.rowSendBtn}
                onClick={() => handleReenviarCorreo(row)}
                disabled={!!accionLoading}
                aria-label={`Reenviar encuesta a ${row.nombre_completo}`}
                title="Reenviar encuesta"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" aria-hidden="true">
                  <path d="M120-160v-640l760 320-760 320Zm80-120 474-200-474-200v140l240 60-240 60v140Zm0 0v-400 400Z" fill="currentColor" />
                </svg>
              </button>
            )}
            {puedeReiniciarFila(row) && (
              <button
                type="button"
                className={styles.rowResetBtn}
                onClick={() => setResetConfirmRow(row)}
                disabled={!!accionLoading}
                aria-label={`Reiniciar respuestas de ${row.nombre_completo}`}
                title="Reiniciar encuesta"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M17.65 6.35a7.95 7.95 0 0 0-5.65-2.35c-4.42 0-8 3.58-8 8h2c0-3.31 2.69-6 6-6 1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35zM6.35 17.65A7.95 7.95 0 0 0 12 20c4.42 0 8-3.58 8-8h-2c0 3.31-2.69 6-6 6-1.66 0-3.14-.69-4.22-1.78L11 13H4v7l2.35-2.35z" fill="currentColor" />
                </svg>
              </button>
            )}
          </div>
          <button
            type="button"
            className={encuestaStyles.resultadosBtn}
            onClick={() => openDetalle(row)}
            aria-label={`Ver resultados de ${row.nombre_completo}`}
          >
            <svg className={encuestaStyles.resultadosBtnIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 20V10" />
              <path d="M12 20V4" />
              <path d="M6 20v-6" />
            </svg>
            <span>Resultados</span>
            <svg className={encuestaStyles.resultadosBtnArrow} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  if (!encuestaIdFromUrl) {
    return <Navigate to="/creatividades/encuestas" replace />;
  }

  if (!loadingEncuesta && !encuestaInfo) {
    return <Navigate to="/creatividades/encuestas" replace />;
  }

  const estadoEncuestaLabel = encuestaInfo
    ? { programada: 'Programada', activa: 'Activa', finalizada: 'Finalizada' }[encuestaInfo.estado] ||
      encuestaInfo.estado
    : '';
  const estadoBadgeClass = encuestaInfo
    ? {
        programada: encuestaStyles.badgeProgramada,
        activa: encuestaStyles.badgeActiva,
        finalizada: encuestaStyles.badgeFinalizada,
      }[encuestaInfo.estado] || encuestaStyles.badgeNeutral
    : encuestaStyles.badgeNeutral;

  const encuestaTitulo = encuestaInfo
    ? encuestaInfo.tipo === 'individual' || encuestaInfo.tipo === 'freemium'
      ? encuestaInfo.nombre_completo || 'Participante'
      : encuestaInfo.nombre_equipo || 'Equipo'
    : '';

  const encuestaPeriodo = encuestaInfo
    ? encuestaInfo.fecha_fin && encuestaInfo.fecha_fin !== encuestaInfo.fecha_inicio
      ? `${formatFecha(encuestaInfo.fecha_inicio)} — ${formatFecha(encuestaInfo.fecha_fin)}`
      : formatFecha(encuestaInfo.fecha_inicio)
    : '';

  return (
    <div className={styles.container}>
      <Link to="/creatividades/encuestas" className={styles.backLink}>
        ← Volver a encuestas
      </Link>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Resultados</h2>
          {encuestaInfo && (
            <div className={styles.encuestaSummary}>
              <div className={styles.encuestaSummaryTop}>
                <p className={styles.encuestaSummaryTitle}>{encuestaTitulo}</p>
                <span className={`${encuestaStyles.badge} ${estadoBadgeClass}`}>
                  {estadoEncuestaLabel}
                </span>
              </div>
              <p className={styles.encuestaSummaryMeta}>
                {encuestaInfo.tipo === 'equipo' && encuestaInfo.nombre_empresa && (
                  <span>{encuestaInfo.nombre_empresa}</span>
                )}
                {encuestaInfo.tipo === 'individual' && (
                  <span>Individual</span>
                )}
                {encuestaInfo.tipo === 'freemium' && (
                  <span>Freemium</span>
                )}
                {encuestaPeriodo && <span>{encuestaPeriodo}</span>}
              </p>
            </div>
          )}
        </div>
        <button
          type="button"
          className={detalleStyles.exportBtn}
          onClick={
            encuestaInfo?.tipo === 'equipo'
              ? handleExportarInformeGrupo
              : handleExportarInformeIndividual
          }
          disabled={!puedeExportarInforme || exportandoInforme}
          title={
            encuestaInfo?.tipo === 'equipo'
              ? 'Descargar el informe grupal del equipo (PPTX)'
              : detalle?.usuario?.id_usuario
                ? 'Descargar informe del participante abierto en detalle'
                : 'Abre el detalle de un participante para exportar su informe'
          }
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="12" y1="18" x2="12" y2="12" />
            <polyline points="9 15 12 18 15 15" />
          </svg>
          {exportandoInforme ? 'Generando…' : 'Exportar informe'}
        </button>
      </div>

      {fetchError && (
        <p className={styles.errorMsg} role="alert">
          {fetchError}
        </p>
      )}

      {successMsg && (
        <p className={styles.successMsg} role="status">
          {successMsg}
        </p>
      )}

      {!loadingResultados && (resumen.total > 0 || resultados.length > 0) && (
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <span className={styles.statValue}>{resumen.total}</span>
            <span className={styles.statLabel}>Participantes</span>
          </div>
          <div className={styles.statCard}>
            <span className={`${styles.statValue} ${styles.statSuccess}`}>{resumen.completados}</span>
            <span className={styles.statLabel}>Completados</span>
          </div>
          <div className={styles.statCard}>
            <span className={`${styles.statValue} ${styles.statWarning}`}>{resumen.enProgreso}</span>
            <span className={styles.statLabel}>En progreso</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statValue}>{resumen.noIniciados}</span>
            <span className={styles.statLabel}>Sin iniciar</span>
          </div>
        </div>
      )}

      {!loadingEncuesta && (
        <div className={styles.toolbar}>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Buscar por nombre…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            aria-label="Buscar participante"
          />
          <select
            className={styles.filterSelect}
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            aria-label="Filtrar por estado"
          >
            <option value="todos">Todos los estados</option>
            <option value="completado">Completado</option>
            <option value="en_progreso">En progreso</option>
            <option value="no_iniciado">No iniciado</option>
          </select>
          <select
            className={styles.filterSelect}
            value={orden}
            onChange={(e) => setOrden(e.target.value)}
            aria-label="Ordenar lista"
          >
            <option value="nombre_asc">Nombre (A → Z)</option>
            <option value="nombre_desc">Nombre (Z → A)</option>
            <option value="porcentaje_desc">Progreso (mayor primero)</option>
            <option value="porcentaje_asc">Progreso (menor primero)</option>
            <option value="estado">Estado</option>
          </select>
          {modo === 'paginado' ? (
            <span className={styles.filterHint}>
              Mostrando página {paginaActual} de {totalPaginas}
              {' · '}
              {totalFilas.toLocaleString('es-ES')} participantes en total
            </span>
          ) : filtradosOrdenados.length !== resultados.length ? (
            <span className={styles.filterHint}>
              Mostrando {filtradosOrdenados.length} de {resultados.length}
            </span>
          ) : null}
        </div>
      )}

      {loadingResultados && (
        <p className={styles.loadingMsg}>Cargando participantes…</p>
      )}

      {!loadingResultados && !loadingEncuesta && (
        <DataTable
          title={`Participantes (${modo === 'paginado' ? totalFilas.toLocaleString('es-ES') : filtradosOrdenados.length})`}
          columns={columns}
          data={filaPagina}
          onRowClick={openDetalle}
          getRowKey={(row) => row.id_usuario}
          compact
          pagination={
            (modo === 'paginado' ? totalFilas : filtradosOrdenados.length) > PAGE_SIZE
              ? {
                  currentPage: paginaActual,
                  totalPages: totalPaginas,
                  onPrev: () => setPagina((p) => Math.max(1, p - 1)),
                  onNext: () => setPagina((p) => Math.min(totalPaginas, p + 1)),
                }
              : undefined
          }
        />
      )}

      {(detalle || loadingDetalle) && (
        <DetalleRespuestaModal
          detalle={detalle}
          loading={loadingDetalle}
          onClose={closeDetalle}
          onExportarInforme={handleExportarInformeIndividual}
          exportandoInforme={exportandoInforme}
        />
      )}

      {exportandoInforme && (
        <div className={styles.exportLoaderOverlay} role="status" aria-live="polite" aria-busy="true">
          <div className={styles.exportLoaderBox}>
            <div className={styles.spinner} aria-hidden="true" />
            <p className={styles.exportLoaderTitle}>
              {tipoExportacionInforme === 'grupal'
                ? 'Generando informe grupal…'
                : 'Generando informe individual…'}
            </p>
            <p className={styles.exportLoaderHint}>
              Esto puede tardar unos segundos. La descarga comenzará automáticamente.
            </p>
          </div>
        </div>
      )}

      {resetConfirmRow && (
        <div className={styles.modalOverlay} onClick={() => !accionLoading && setResetConfirmRow(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, padding: '24px 28px' }}>
            <div className={styles.modalHeader}>
              <h3>Reiniciar encuesta</h3>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={() => !accionLoading && setResetConfirmRow(null)}
                aria-label="Cerrar"
                disabled={!!accionLoading}
              >
                ✕
              </button>
            </div>
            <p className={styles.confirmText}>
              ¿Reiniciar las respuestas de <strong>{resetConfirmRow.nombre_completo}</strong>? Se eliminarán todas sus respuestas y podrá volver a completarla desde el principio.
            </p>
            <div className={styles.modalFormActions}>
              <button
                type="button"
                className={styles.actionBtn}
                onClick={() => setResetConfirmRow(null)}
                disabled={!!accionLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={styles.dangerBtn}
                onClick={() => handleReiniciarEncuesta(resetConfirmRow)}
                disabled={isAccionLoading('reiniciar', resetConfirmRow.id_usuario)}
              >
                {isAccionLoading('reiniciar', resetConfirmRow.id_usuario) ? 'Reiniciando…' : 'Reiniciar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Respuestas;
